import { createFileRoute } from "@tanstack/react-router";
import { ArrowRightLeft, Check, Copy, Gift, Share2, Users, Wallet } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { formatUsd, useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import {
  useReferralMe,
  useReferralWithdrawals,
  useTransferReferralBalance,
  useWithdrawReferralBalance,
  type ReferralWithdrawal,
} from "@/lib/queries";
import { copyToClipboard } from "@/lib/utils";

export const Route = createFileRoute("/_app/referrals")({
  component: ReferralsPage,
  head: () => ({
    meta: [
      { title: "Referrals — SmsEmpire" },
      { name: "description", content: "Earn money by inviting others to SmsEmpire." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

// Same whitelist the backend enforces, repeated here only so a typo is caught before
// the request leaves. The server remains the authority.
const TRC20_ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

function statusClass(status: ReferralWithdrawal["status"]) {
  switch (status) {
    case "paid":
      return "bg-emerald-500/15 text-emerald-500 border-emerald-500/30";
    case "rejected":
      return "bg-destructive/15 text-destructive border-destructive/30";
    default:
      return "bg-amber-500/15 text-amber-500 border-amber-500/30";
  }
}

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (await copyToClipboard(value)) {
      setCopied(true);
      toast.success(t("profile.copied_toast", { label }));
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error(t("profile.copy_failed_toast", { code: value }));
    }
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 select-all truncate rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs">
          {value}
        </code>
        <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={copy}>
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          <span>{copied ? t("profile.copied") : t("profile.copy_link")}</span>
        </Button>
      </div>
    </div>
  );
}

function WithdrawDialog({
  open,
  onOpenChange,
  balance,
  minWithdrawal,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  balance: number;
  minWithdrawal: number;
}) {
  const { t } = useI18n();
  const { format } = useCurrency();
  const withdraw = useWithdrawReferralBalance();
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");

  const parsed = Number(amount);
  const amountValid =
    Number.isFinite(parsed) && parsed > 0 && parsed <= balance && parsed >= minWithdrawal;
  const walletValid = TRC20_ADDRESS.test(wallet.trim());

  const submit = async () => {
    try {
      await withdraw.mutateAsync({ amount: parsed, wallet_address: wallet.trim() });
      toast.success(t("referral.withdraw_requested"));
      onOpenChange(false);
      setAmount("");
      setWallet("");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("referral.withdraw_failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("referral.withdraw_title")}</DialogTitle>
          <DialogDescription>
            {t("referral.withdraw_intro", { min: format(minWithdrawal) })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="withdraw-amount">{t("referral.withdraw_amount_label")}</Label>
            <Input
              id="withdraw-amount"
              type="number"
              step="0.01"
              min={minWithdrawal}
              max={balance}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={String(minWithdrawal)}
            />
            <p className="text-xs text-muted-foreground">
              {t("referral.available_amount", { amount: format(balance) })}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="withdraw-wallet">{t("referral.withdraw_wallet_label")}</Label>
            <Input
              id="withdraw-wallet"
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
              placeholder="T..."
              className="font-mono text-xs"
              autoComplete="off"
              spellCheck={false}
            />
            {wallet.trim() !== "" && !walletValid && (
              <p className="text-xs text-destructive">{t("referral.invalid_wallet")}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={!amountValid || !walletValid || withdraw.isPending}>
            {t("referral.withdraw_submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReferralsPage() {
  const { t } = useI18n();
  const { format } = useCurrency();
  const { data, isLoading } = useReferralMe();
  const { data: withdrawals = [] } = useReferralWithdrawals();
  const transfer = useTransferReferralBalance();
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const runTransfer = async () => {
    try {
      const result = await transfer.mutateAsync();
      toast.success(t("referral.transfer_done", { amount: format(result.balance) }));
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("referral.transfer_failed"));
    }
  };

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const link =
    typeof window !== "undefined"
      ? `${window.location.origin}/ref/${data.referral_code}`
      : `/ref/${data.referral_code}`;
  const hasBalance = data.referral_balance > 0;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("referral.page_title")}</h1>
        <p className="mt-1 text-muted-foreground">
          {t("referral.page_subtitle", {
            // Deliberately not `format`: the reward is a USD promise, see formatUsd.
            amount: formatUsd(data.reward_amount),
            min: formatUsd(data.referral_min_recharge),
          })}
        </p>
      </div>

      {!data.referral_enabled && (
        <Card className="border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-600 dark:text-amber-400">
          {t("referral.disabled_notice")}
        </Card>
      )}

      {/* Referral wallet */}
      <Card className="border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">{t("referral.wallet_title")}</h2>
            <p className="text-xs text-muted-foreground">{t("referral.wallet_desc")}</p>
          </div>
        </div>

        <p className="text-4xl font-bold text-emerald-500">{format(data.referral_balance)}</p>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button
            onClick={runTransfer}
            disabled={!hasBalance || transfer.isPending}
            className="gap-2"
          >
            <ArrowRightLeft className="h-4 w-4" />
            {t("referral.transfer_button")}
          </Button>
          <Button
            variant="outline"
            onClick={() => setWithdrawOpen(true)}
            disabled={!hasBalance || !data.referral_enabled}
            className="gap-2"
          >
            <Gift className="h-4 w-4" />
            {t("referral.withdraw_button", {
              currency: data.withdrawal_currency,
              network: data.withdrawal_network,
            })}
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t("referral.withdraw_manual_notice")}</p>
      </Card>

      {/* Code and link */}
      <Card className="border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <Share2 className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t("referral.share_title")}</h2>
        </div>
        <div className="space-y-3">
          <CopyField label={t("profile.referral_link_label")} value={link} />
          <CopyField label={t("profile.referral_code_label")} value={data.referral_code} />
        </div>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-border bg-card p-4 text-center">
          <Users className="mx-auto mb-1 h-5 w-5 text-primary" />
          <p className="text-2xl font-bold">{data.referred_count}</p>
          <p className="text-xs text-muted-foreground">{t("profile.referred_users")}</p>
        </Card>
        <Card className="border-border bg-card p-4 text-center">
          <p className="text-2xl font-bold text-amber-500">{data.pending_credits}</p>
          <p className="text-xs text-muted-foreground">{t("profile.pending_credits")}</p>
        </Card>
        <Card className="border-border bg-card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-500">{format(data.lifetime_credits)}</p>
          <p className="text-xs text-muted-foreground">{t("profile.total_earned")}</p>
        </Card>
      </div>

      {/* Withdrawal history */}
      <Card className="border-border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">{t("referral.history_title")}</h2>
        {withdrawals.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("referral.history_empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">{t("referral.history_date")}</th>
                  <th className="pb-2 pr-4 font-medium">{t("referral.history_amount")}</th>
                  <th className="pb-2 pr-4 font-medium">{t("referral.history_wallet")}</th>
                  <th className="pb-2 font-medium">{t("referral.history_status")}</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">
                      {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2.5 pr-4 font-medium">{format(w.amount)}</td>
                    <td className="max-w-[12rem] truncate py-2.5 pr-4 font-mono text-xs text-muted-foreground">
                      {w.wallet_address}
                    </td>
                    <td className="py-2.5">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-xs ${statusClass(w.status)}`}
                      >
                        {t(`referral.status_${w.status}`)}
                      </span>
                      {w.status === "rejected" && w.admin_note && (
                        <p className="mt-1 text-xs text-muted-foreground">{w.admin_note}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <WithdrawDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        balance={data.referral_balance}
        minWithdrawal={data.min_withdrawal}
      />
    </div>
  );
}
